/**
 * Cognara AI brief — on demand, at any drill-down level.
 *
 * Nothing fires on a drill-down click. A brief costs real money per call, so it
 * is produced when somebody asks for it, and an unchanged metric payload
 * returns the stored brief instead of paying twice. Every brief is persisted
 * with a hash of the exact figures it was shown, so one quoted in a client
 * meeting can be reproduced afterwards.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, RefreshCw, Sparkles, TriangleAlert } from 'lucide-react';
import api from '@/lib/api';
import { int, stamp, useIptTheme } from './iptTheme';
import { Btn, Panel } from './IptPrimitives';

const TONE_KEY = { red: 'red', amber: 'amber', green: 'green', neutral: 'muted' };
const URGENCY = { now: 'red', 'this week': 'amber', 'this month': 'primary' };

/**
 * A brief takes about 90 seconds. The job runs on the server, so changing tabs
 * must not abandon it — the panel records the run in a module-level registry
 * that outlives its own unmount, and resumes polling the moment it comes back.
 */
const RUNS = new Map();
const runKey = (o) => JSON.stringify([o.level, o.projectId, o.monthBasis, o.dim,
  o.path || null, o.invoiceId || null, o.transitionId || null, o.filters || {}]);

export const CognaraPanel = ({ level, projectId, monthBasis, dim, filters, subtitle,
  path, invoiceId, transitionId }) => {
  const T = useIptTheme();
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const poll = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const key = runKey({ level, projectId, monthBasis, dim, path, invoiceId,
    transitionId, filters });

  // a change of level or scope invalidates the brief on screen — it was written
  // about different figures
  useEffect(() => {
    setDoc(null);
    setErr(null);
    if (poll.current) clearInterval(poll.current);
  }, [level, projectId, monthBasis, dim, path, invoiceId, transitionId,
    JSON.stringify(filters || {})]);

  const watch = useCallback((briefId, startedAt) => {
    if (poll.current) clearInterval(poll.current);
    setBusy(true);
    const tick = async () => {
      try {
        const j = await api.get(`/ipt/analytics/cognara/job/${briefId}`);
        setDoc(j.data);
        setElapsed(Math.round((Date.now() - startedAt) / 1000));
        if (j.data.status !== 'running') {
          clearInterval(poll.current);
          setBusy(false);
          RUNS.delete(key);
          if (j.data.status === 'failed') setErr(j.data.error);
        }
      } catch {
        clearInterval(poll.current);
        setBusy(false);
        setErr('Lost contact with Cognara while it was working');
      }
    };
    tick();
    poll.current = setInterval(tick, 4000);
  }, [key]);

  // the job outlives this component — pick it back up rather than starting again
  useEffect(() => {
    const run = RUNS.get(key);
    if (run) watch(run.brief_id, run.startedAt);
  }, [key, watch]);

  useEffect(() => () => { if (poll.current) clearInterval(poll.current); }, []);

  const ask = useCallback(async (refresh = false) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post('/ipt/analytics/cognara', {
        level, project_id: projectId, month_basis: monthBasis, dim,
        filters: filters || {}, refresh,
        path, invoice_id: invoiceId, transition_id: transitionId,
      });
      setDoc(r.data);
      if (r.data.status === 'running') {
        const startedAt = Date.now();
        RUNS.set(key, { brief_id: r.data.brief_id, startedAt });
        watch(r.data.brief_id, startedAt);
      } else {
        setBusy(false);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Cognara could not be reached');
      setBusy(false);
    }
  }, [level, projectId, monthBasis, dim, filters, path, invoiceId, transitionId]);

  const b = doc?.brief;
  const running = doc?.status === 'running' || (busy && !b);
  const reported = typeof doc?.progress === 'number' && doc.progress > 5;
  const expected = doc?.expectedSeconds || 90;
  const estimate = Math.min(95, Math.round((100 * elapsed) / expected));
  // real progress when the brief has reported a stage, elapsed estimate before that,
  // and never let the estimate overtake what the brief has actually confirmed
  const pct = reported ? Math.max(doc.progress, Math.min(estimate, 95)) : Math.max(estimate, 5);

  return (
    <Panel T={T} testId={`cognara-${level}`} title="Cognara AI"
      subtitle={subtitle || 'Reads the exact figures on this screen'}
      right={(
        <div className="flex items-center gap-2">
          {doc?.cached && (
            <span className="text-[9.5px] px-1.5 py-0.5 rounded" data-testid="cognara-cached"
              style={{ background: T.tint(T.green, '1A'), color: T.green }}>
              unchanged — stored brief
            </span>
          )}
          {b && (
            <>
              {/* two separate controls, because they cost different things:
                  asking again reuses the stored brief when the figures have not
                  moved; regenerating pays for a fresh one. */}
              <Btn T={T} variant="ghost" onClick={() => ask(false)} disabled={busy}
                testId="cognara-ask">
                <Sparkles className="w-3.5 h-3.5" /> Ask again
              </Btn>
              <Btn T={T} variant="ghost" onClick={() => ask(true)} disabled={busy}
                testId="cognara-refresh"><RefreshCw className="w-3.5 h-3.5" /> Regenerate</Btn>
            </>
          )}
          {!b && (
            <Btn T={T} onClick={() => ask(false)} disabled={busy} testId="cognara-ask">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Sparkles className="w-3.5 h-3.5" />}
              {running ? 'Reading the figures…' : 'Ask Cognara'}
            </Btn>
          )}
        </div>
      )}>
      {!doc && !err && (
        <p className="text-[11.5px] leading-relaxed max-w-[720px]" style={{ color: T.body }}>
          Cognara reads this level's metric payload and writes the brief for it — the cash
          position, the binding constraint, where the delay sat and what to do about it. It
          is asked rather than automatic, because each brief is a real cost and one nobody
          read is money spent on nothing. Every figure it quotes carries the payload path it
          came from, so you can check it against the tiles above.
        </p>
      )}

      {running && (
        <div className="py-2" data-testid="cognara-running">
          <div className="flex items-center gap-2 text-[11.5px]" style={{ color: T.body }}>
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: T.primary }} />
            <span data-testid="cognara-stage">
              {doc?.stage || 'reading the metric payload'}
            </span>
            <span style={{ color: T.muted }}>
              · {int(doc?.input_keys?.length || 0)} metric groups
            </span>
            <span className="ml-auto tabular-nums font-semibold"
              data-testid="cognara-progress-pct" style={{ color: T.primary }}>
              {pct}%
            </span>
          </div>
          <div className="h-1.5 rounded-full mt-1.5 overflow-hidden"
            data-testid="cognara-progress-bar" style={{ background: T.border }}>
            <div className="h-full transition-[width] duration-700 ease-out"
              style={{ width: `${pct}%`, background: T.primary }} />
          </div>
          <div className="flex items-center gap-2 mt-1 text-[9.5px]" style={{ color: T.muted }}>
            <span data-testid="cognara-elapsed">
              {int(elapsed)}s elapsed of about {int(doc?.expectedSeconds || 90)}s
            </span>
            <span>
              {reported
                ? '· reported by the brief itself'
                : '· estimated from elapsed time until the brief reports a stage'}
            </span>
            <span className="ml-auto">
              · it keeps running if you change tab
            </span>
          </div>
        </div>
      )}

      {err && (
        <div className="flex items-start gap-2 text-[11.5px] py-2" data-testid="cognara-error"
          style={{ color: T.red }}>
          <TriangleAlert className="w-4 h-4 mt-px shrink-0" />
          <span>{String(err)}
            <button type="button" onClick={() => ask(true)}
              className="ml-2 underline" style={{ color: T.primary }}>try again</button>
          </span>
        </div>
      )}

      {b && (
        <div className="space-y-3" data-testid="cognara-brief">
          <div className="rounded-md px-3 py-2.5"
            style={{ background: T.tint(T.primary, '12'),
                     border: `1px solid ${T.tint(T.primary, '33')}` }}>
            <p className="text-[13px] font-semibold leading-snug" data-testid="cognara-headline"
              style={{ color: T.ink }}>{b.headline}</p>
            <p className="text-[10px] mt-1.5" style={{ color: T.muted }}>
              <b style={{ color: { high: T.green, medium: T.amber, low: T.red }[b.confidence] }}>
                {b.confidence} confidence
              </b>{' — '}{b.confidence_reason}
            </p>
          </div>

          {(b.sections || []).map((s) => (
            <div key={s.id} data-testid={`cognara-section-${s.id}`} className="rounded-md p-3"
              style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
              <h4 className="text-[12px] font-bold" style={{ color: T.ink }}>{s.title}</h4>
              <p className="text-[11.5px] leading-relaxed mt-1" style={{ color: T.body }}>
                {s.summary}
              </p>
              {(s.metrics || []).length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2.5">
                  {s.metrics.map((m, i) => {
                    const c = { red: T.red, amber: T.amber, green: T.green,
                      muted: T.muted }[TONE_KEY[m.tone] || 'muted'];
                    return (
                      <div key={i} className="rounded px-2 py-1.5"
                        style={{ background: T.surface, borderLeft: `3px solid ${c}` }}>
                        <div className="text-[9px] uppercase tracking-wider"
                          style={{ color: T.muted }}>{m.label}</div>
                        <div className="text-[11.5px] font-semibold tabular-nums leading-tight mt-0.5"
                          style={{ color: c }}>{m.value}</div>
                        <div className="text-[8.5px] font-mono mt-0.5 break-all"
                          style={{ color: T.muted }}>{m.source}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              {(s.insights || []).length > 0 && (
                <ul className="mt-2 space-y-1">
                  {s.insights.map((x, i) => (
                    <li key={i} className="text-[11px] leading-relaxed flex gap-1.5"
                      style={{ color: T.body }}>
                      <span style={{ color: T.accent }}>▸</span><span>{x}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {(b.actions || []).length > 0 && (
            <div data-testid="cognara-actions">
              <h4 className="text-[11px] font-bold uppercase tracking-[0.08em] mb-1.5"
                style={{ color: T.muted }}>Recommended actions</h4>
              <div className="space-y-1.5">
                {b.actions.map((a, i) => {
                  const c = { red: T.red, amber: T.amber, primary: T.primary }[
                    URGENCY[a.urgency] || 'primary'];
                  return (
                    <div key={i} className="rounded-md px-2.5 py-2 flex items-start gap-2"
                      style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                      <span className="px-1.5 py-0.5 rounded text-[8.5px] font-bold uppercase whitespace-nowrap mt-px"
                        style={{ background: T.tint(c, '1F'), color: c }}>{a.urgency}</span>
                      <div className="min-w-0">
                        <p className="text-[11.5px] font-semibold" style={{ color: T.ink }}>
                          {a.action}
                        </p>
                        <p className="text-[10px] mt-0.5" style={{ color: T.body }}>
                          <b>{a.owner}</b> — {a.why}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(b.caveats || []).length > 0 && (
            <details data-testid="cognara-caveats" className="rounded-md px-2.5 py-2"
              style={{ background: T.surfaceAlt, border: `1px dashed ${T.borderStrong}` }}>
              <summary className="text-[10.5px] font-bold cursor-pointer"
                style={{ color: T.muted }}>
                What a sceptical reader would challenge ({b.caveats.length})
              </summary>
              <ul className="mt-1.5 space-y-1">
                {b.caveats.map((c, i) => (
                  <li key={i} className="text-[10.5px] leading-relaxed"
                    style={{ color: T.body }}>— {c}</li>
                ))}
              </ul>
            </details>
          )}

          <p className="text-[9.5px] pt-1.5 flex items-center gap-1.5 flex-wrap"
            data-testid="cognara-provenance"
            style={{ color: T.muted, borderTop: `1px dashed ${T.border}` }}>
            <CheckCircle2 className="w-3 h-3" style={{ color: T.green }} />
            {doc.engine} · {stamp(doc.generated_on)} · payload fingerprint{' '}
            <code className="font-mono">{String(doc.payload_hash || '').slice(0, 12)}</code>
            {' '}· stored, so this brief can be reproduced against the exact figures it was
            shown{doc.truncated ? ' · WARNING: the brief hit its length ceiling' : ''}
          </p>
        </div>
      )}
    </Panel>
  );
};

export default CognaraPanel;
